import type { CollectionsEntitlementsQuery$data, EntitlementsInfo } from '../../types';

/*
 the capabilities endpoint returns the products in ARI e.g. `ari:cloud:jira-software::site/TEST--80b8bb0e-3587-4dd9-b86f-e15e3fd152ea`
 the productId is split from the full description.
*/
export const twcProductKeys = ['confluence', 'jira-software', 'loom'];

export const entitlementsQuery = `
	query CollectionsContextEntitlementsQuery($cloudId: ID!, $skip: Boolean!) {
		tenantContexts(cloudIds: [$cloudId]) @skip(if: $skip) {
			confluenceEntitlementInfo: entitlementInfo(hamsProductKey: "confluence") {
				entitlement {
					relatesFromEntitlements {
						relationshipId
						entitlementId
						relationshipType
					}
				}
			}
			loomEntitlementInfo: entitlementInfo(hamsProductKey: "loom") {
				entitlement {
					relatesFromEntitlements {
						relationshipId
						entitlementId
						relationshipType
					}
				}
			}
			jiraEntitlementInfo: entitlementInfo(hamsProductKey: "jira-software") {
				entitlement {
					relatesFromEntitlements {
						relationshipId
						entitlementId
						relationshipType
					}
				}
			}
		}
	}
`;


// https://bitbucket.org/atlassian/commerce-experience/src/master/packages/commerce/ccp-keys/src/offering-relationship-keys.ts
const TWC_OFFERING_RELATIONSHIP_IDS: string[] = [
	'9038a217-36b8-4272-a310-d595d2d3dacf', // free - staging
	'db8aa835-3e17-4774-98b0-e31f9b880b64', // free - production
	'b3a4aea9-5194-43bd-b36c-0b9c74ebe8d4', // standard - staging
	'4ecc43de-6a5e-4e35-a875-ac178f32fdd9', // standard - production
	'58381e9e-d3ac-4886-9813-acb4fe383375', // premium - staging
	'2b7e0a98-3bed-4e80-93c1-4a035d45a60f', // premium - production
	'd30dd83c-a8f0-4304-9e83-5f2640eeab1f', // enterprise - staging
	'64f91952-23bd-43ef-a600-8af6b34a6394', // enterprise - production
] as const;

export const getHasTeamworkCollection = (
	entitlements: CollectionsEntitlementsQuery$data,
): boolean => {
	const { tenantContexts } = entitlements || {};
	if (tenantContexts?.length !== 1) {
		return false;
	}
	const [twcContext] = tenantContexts;
	const getCollectionEntitlements = (
		info?: EntitlementsInfo | null,
	): { relationshipId: string; entitlementId: string }[] =>
		info?.entitlement?.relatesFromEntitlements
			?.map((entitlement) => {
				const { relationshipType, relationshipId, entitlementId } = entitlement || {};

				if (!relationshipId || !relationshipType || !entitlementId) {
					return undefined;
				}
				if (relationshipType !== 'COLLECTION') {
					return undefined;
				}
				if (!TWC_OFFERING_RELATIONSHIP_IDS.includes(relationshipId)) {
					return undefined;
				}

				return { entitlementId, relationshipId };
			})
			.filter((entitlement) => entitlement !== undefined) || [];

	const [first, ...rest] = [
		twcContext?.confluenceEntitlementInfo,
		twcContext?.jiraEntitlementInfo,
		twcContext?.loomEntitlementInfo,
	].map(getCollectionEntitlements);

	// For each relationshipId, check if all products have the same entitlementId
	for (const { relationshipId, entitlementId } of first) {
		if (!relationshipId || !entitlementId) {
			continue;
		}
		// Check if this relationshipId exists in all products with the same entitlementId
		const existsInAll = rest.every((productEntitlements) =>
			productEntitlements.some(
				(e) => e.relationshipId === relationshipId && e.entitlementId === entitlementId,
			),
		);
		if (existsInAll) {
			return true;
		}
	}
	return false;
};
